import React, { useMemo, useRef, useState } from 'react'
import stringSimilarity from 'string-similarity'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { Button, Card, Col, Row } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import { useThemeColors } from '../../../../../../../../hooks/useThemeColors'
import ConnectionLines from './ConnectionLines'

const MatchingForm = ({
    originalItems: originalItemsProp = [],
    matchedItems: matchedItemsProp = [],
    renderItem,
    onSubmit,
    onRevert,
    matchingProperty = 'matchingId',
    titleLeft = 'Original Items',
    titleRight = 'Matched Items',
    titleLeftStyle = {},
    titleRightStyle = {},
    submitButtonText = 'Submit',
    getItemString = item => item.name || item.label || '',
}) => {
    const originalItems = useMemo(
        () =>
            originalItemsProp.map(item => ({
                ...item,
                id: String(item.id),
            })),
        [originalItemsProp],
    )

    // Define autoMatchItems function once
    const autoMatchItems = matchedItemsList => {
        const usedOriginalItemIds = new Set()
        return matchedItemsList.map(matchedItem => {
            const itemWithId = {
                ...matchedItem,
                id: String(matchedItem.id),
            }
            if (itemWithId[matchingProperty]) {
                usedOriginalItemIds.add(itemWithId[matchingProperty])
                return itemWithId
            }
            const matchedItemString = getItemString(itemWithId)
            const availableOriginalItems = originalItems.filter(item => !usedOriginalItemIds.has(item.id))
            const originalItemsStrings = availableOriginalItems.map(getItemString)
            if (originalItemsStrings.length === 0) {
                return itemWithId
            }
            const matches = stringSimilarity.findBestMatch(matchedItemString, originalItemsStrings)
            const bestMatchIndex = matches.bestMatchIndex
            const bestMatchScore = matches.bestMatch.rating
            if (bestMatchScore > 0.5) {
                const bestMatchOriginalItem = availableOriginalItems[bestMatchIndex]
                usedOriginalItemIds.add(bestMatchOriginalItem.id)
                return {
                    ...itemWithId,
                    [matchingProperty]: String(bestMatchOriginalItem.id),
                }
            } else {
                return itemWithId
            }
        })
    }

    const [matchedItems, setMatchedItems] = useState(() => {
        const initialMatchedItems = matchedItemsProp.map(item => ({
            ...item,
            id: String(item.id),
        }))
        return autoMatchItems(initialMatchedItems)
    })

    const containerRef = useRef(null)

    const onDragEnd = result => {
        if (!result.destination) return

        const sourceIndex = result.source.index
        const destinationId = result.destination.droppableId
        if (destinationId === 'matched-list') return

        const updatedMatchedItems = [...matchedItems]
        const [movedItem] = updatedMatchedItems.splice(sourceIndex, 1)

        const existingMatchIndex = updatedMatchedItems.findIndex(item => item[matchingProperty] === destinationId)
        if (existingMatchIndex !== -1) {
            updatedMatchedItems[existingMatchIndex][matchingProperty] = null
        }

        const destinationIndex = originalItems.findIndex(item => item.id === destinationId)

        movedItem[matchingProperty] = destinationId
        updatedMatchedItems.splice(destinationIndex, 0, movedItem)

        setMatchedItems(updatedMatchedItems)
    }

    const handleUndo = matchedId => {
        const updatedMatchedItems = matchedItems.map(item =>
            item.id === matchedId
                ? {
                      ...item,
                      [matchingProperty]: null,
                  }
                : item,
        )
        setMatchedItems(updatedMatchedItems)
    }

    const handleSubmit = () => {
        onSubmit({ originalItems, matchedItems })
    }

    const handleRevert = () => {
        const resetMatchedItems = autoMatchItems(
            matchedItemsProp.map(item => ({
                ...item,
                id: String(item.id),
            })),
        )
        setMatchedItems(resetMatchedItems)
        if (onRevert) {
            onRevert()
        }
    }

    const colors = useThemeColors()
    const noMatchesSet = matchedItems.every(item => !item[matchingProperty])

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <DragDropContext onDragEnd={onDragEnd}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Title level={4} style={titleLeftStyle}>
                            {titleLeft}
                        </Title>
                        {originalItems.map(item => (
                            <Droppable key={item.id} droppableId={item.id}>
                                {(provided, snapshot) => (
                                    <Card
                                        id={`orig-${item.id}`}
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        size='small'
                                        style={{
                                            marginBottom: 16,
                                            border: snapshot.isDraggingOver ? `1px dashed ${colors.primary}` : undefined,
                                        }}
                                    >
                                        {renderItem(item)}
                                    </Card>
                                )}
                            </Droppable>
                        ))}
                    </Col>
                    <Col span={12}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Title level={4} style={titleRightStyle}>
                                {titleRight}
                            </Title>
                            <Text type='secondary'>Drag and drop to set matches</Text>
                        </div>
                        <Droppable droppableId='matched-list'>
                            {provided => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                    {matchedItems.map((item, index) => (
                                        <Draggable key={item.id} draggableId={item.id} index={index}>
                                            {provided => (
                                                <Card
                                                    id={`matched-${item.id}`}
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        marginBottom: 16,
                                                        border: item[matchingProperty]
                                                            ? `1px solid ${colors.primary}`
                                                            : undefined,
                                                    }}
                                                    size='small'
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {renderItem(item)}
                                                        {item[matchingProperty] && (
                                                            <Button
                                                                icon={<CloseOutlined />}
                                                                size='small'
                                                                onClick={() => handleUndo(item.id)}
                                                                style={{ height: '100%' }}
                                                                type='text'
                                                            />
                                                        )}
                                                    </div>
                                                </Card>
                                            )}
                                        </Draggable>
                                    ))}
                                </div>
                            )}
                        </Droppable>
                    </Col>
                </Row>
            </DragDropContext>
            <ConnectionLines
                matchedItems={matchedItems}
                matchingProperty={matchingProperty}
                containerRef={containerRef}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button danger onClick={handleRevert} disabled={noMatchesSet}>
                    Revert
                </Button>
                <Button type='primary' onClick={handleSubmit} disabled={noMatchesSet}>
                    {submitButtonText}
                </Button>
            </div>
        </div>
    )
}

export default MatchingForm
